/**
 * HbarReleaseBanner — V2.J.3
 *
 * Surfaces HBAR treasury monitoring on the Hedera Monitor page in two
 * complementary modes :
 *
 *  ▸ "release"     — a release was detected in the last `maxAgeDays`
 *                    days. Highlights delta + before/after supply +
 *                    relative timestamp + action buttons.
 *
 *  ▸ "surveillance" — no recent release yet. Communicates that we ARE
 *                    actively monitoring + shows the next expected
 *                    release (Q2 2026 ~3.97 B HBAR per Hedera Council
 *                    treasury schedule) + a live countdown. Keeps the
 *                    page useful pre-event.
 *
 * Falls back to nothing only when the cron hasn't run at all yet AND
 * we can't compute a countdown (defensive — should be rare).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { useHbarReleaseEvents } from '../../hooks/useHbarReleaseEvents';

interface HbarReleaseBannerProps {
  /** Max age (days) before the "release" banner switches back to
   *  "surveillance". Default 7 — the release stays prominent for a
   *  week, then we revert to tracking mode. */
  maxAgeDays?: number;
}

// ── Next expected release (Hedera Council treasury schedule) ──────
// Source: hederacouncil.org/treasury. ~3.97 B HBAR expected late
// Q2 2026 — the brief indicates this could land as ~10 sub-events of
// ~400 M each. Update this constant when the next schedule lands.
const NEXT_RELEASE = {
  expectedAt: '2026-06-30T00:00:00Z',
  estimatedHbar: 3_970_000_000,
  // Used to label the next expected event in the banner.
  scheduleLabel: 'T2 2026',
};

export function HbarReleaseBanner({ maxAgeDays = 7 }: HbarReleaseBannerProps) {
  const { t, i18n } = useTranslation();
  const { latest, isLoading } = useHbarReleaseEvents({ limit: 1 });

  // Decide mode : "release" if latest event is recent, "surveillance"
  // otherwise. Loading shows the surveillance frame too (skeleton-ish)
  // so the layout doesn't jump when the data lands.
  const mode = useMemo<'release' | 'surveillance'>(() => {
    if (!latest) return 'surveillance';
    const ageMs = Date.now() - new Date(latest.detected_at).getTime();
    return ageMs <= maxAgeDays * 24 * 3600 * 1000 ? 'release' : 'surveillance';
  }, [latest, maxAgeDays]);

  const locale = i18n?.language || 'fr';
  const fmt = (n: number) =>
    n.toLocaleString(locale === 'fr' ? 'fr-FR' : locale, { maximumFractionDigits: 0 });

  if (mode === 'release' && latest) {
    return (
      <ReleaseDetectedBanner
        delta={latest.delta_hbar}
        previous={latest.previous_released_hbar}
        next={latest.new_released_hbar}
        total={latest.total_hbar}
        detectedAt={latest.detected_at}
        t={t}
        locale={locale}
        fmt={fmt}
      />
    );
  }

  return (
    <SurveillanceBanner
      latest={latest}
      isLoading={isLoading}
      t={t}
      locale={locale}
      fmt={fmt}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────
// Mode 1 — "Release detected"
// ─────────────────────────────────────────────────────────────────────

interface ReleaseDetectedBannerProps {
  delta: number;
  previous: number;
  next: number;
  total: number;
  detectedAt: string;
  t: (key: string, fallback: string) => string;
  locale: string;
  fmt: (n: number) => string;
}

function ReleaseDetectedBanner({
  delta,
  previous,
  next,
  total,
  detectedAt,
  t,
  locale,
  fmt,
}: ReleaseDetectedBannerProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        background:
          'linear-gradient(135deg, rgba(0, 194, 255, 0.12), rgba(0, 0, 0, 0.4))',
        border: '1px solid rgba(0, 194, 255, 0.35)',
        borderRadius: '12px',
        padding: '16px 18px',
        marginBottom: '20px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '14px',
        alignItems: 'center',
      }}
    >
      <BannerIcon variant="release" />

      <div style={{ flex: 1, minWidth: '240px' }}>
        <div
          style={{
            fontSize: '13px',
            fontWeight: 700,
            color: '#00C2FF',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '4px',
          }}
        >
          {t('hedera.release.title', 'Libération HBAR détectée')}
        </div>
        <div style={{ fontSize: '15px', color: 'var(--text)', fontWeight: 600 }}>
          +{fmt(delta)} HBAR
          <span
            style={{
              fontSize: '12px',
              color: 'var(--muted)',
              fontWeight: 500,
              marginLeft: '10px',
            }}
          >
            {relativeAgo(detectedAt, locale)}
          </span>
        </div>
        <div
          style={{
            fontSize: '12px',
            color: 'var(--muted)',
            marginTop: '4px',
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <SupplyCell label={t('hedera.release.before', 'Avant')} value={fmt(previous)} />
          <SupplyCell label={t('hedera.release.after', 'Après')} value={fmt(next)} />
          <SupplyCell label={t('hedera.release.total', 'Total')} value={fmt(total)} />
        </div>
      </div>

      <BannerActions t={t} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Mode 2 — "Surveillance active" (no recent release)
// ─────────────────────────────────────────────────────────────────────

interface SurveillanceBannerProps {
  latest: { new_released_hbar: number; total_hbar: number; detected_at: string } | null;
  isLoading: boolean;
  t: (key: string, fallback: string) => string;
  locale: string;
  fmt: (n: number) => string;
}

function SurveillanceBanner({ latest, isLoading, t, locale, fmt }: SurveillanceBannerProps) {
  const countdown = useCountdown(NEXT_RELEASE.expectedAt);
  // Total supply fallback — if the cron hasn't run yet we use 50 B
  // (HBAR is hard-capped). released uses the latest cron value if any.
  const totalHbar = latest?.total_hbar ?? 50_000_000_000;
  const releasedHbar = latest?.new_released_hbar ?? 43_310_000_000;
  const progressPct = (releasedHbar / totalHbar) * 100;
  const remainingHbar = totalHbar - releasedHbar;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        background:
          'linear-gradient(135deg, rgba(0, 194, 255, 0.06), rgba(0, 0, 0, 0.3))',
        border: '1px solid rgba(0, 194, 255, 0.18)',
        borderRadius: '12px',
        padding: '16px 18px',
        marginBottom: '20px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '14px',
        alignItems: 'center',
      }}
    >
      <BannerIcon variant="surveillance" />

      <div style={{ flex: 1, minWidth: '240px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            fontWeight: 700,
            color: '#00C2FF',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '6px',
          }}
        >
          <PulseDot />
          {t('hedera.surveillance.title', 'Surveillance trésorerie HBAR — active')}
        </div>

        <div style={{ fontSize: '13px', color: 'var(--text)', lineHeight: 1.5 }}>
          {t(
            'hedera.surveillance.description',
            'Nous surveillons les libérations depuis la trésorerie Hedera toutes les heures. Vous recevrez un email dès qu\'une libération significative est détectée.',
          )}
        </div>

        {/* Progress bar : released vs total supply */}
        <div
          style={{
            marginTop: '10px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '6px',
            overflow: 'hidden',
            height: '6px',
            width: '100%',
          }}
          aria-hidden
        >
          <div
            style={{
              width: `${Math.min(100, progressPct)}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #00C2FF, #4F46E5)',
              transition: 'width 0.5s ease',
            }}
          />
        </div>

        <div
          style={{
            fontSize: '11px',
            color: 'var(--muted)',
            marginTop: '6px',
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <span>
            {isLoading
              ? '…'
              : `${t('hedera.surveillance.released', 'Libéré')} : ${fmt(releasedHbar)} HBAR (${progressPct.toFixed(1)} %)`}
          </span>
          <span>
            {t('hedera.surveillance.remaining', 'Restant')} : {fmt(remainingHbar)} HBAR
          </span>
        </div>

        {/* Next expected release — only if the date is still in the
            future. Once we pass it, the cron will surface the real
            event and this hint becomes irrelevant. */}
        {countdown && (
          <div
            style={{
              marginTop: '10px',
              padding: '10px 12px',
              background: 'rgba(0, 194, 255, 0.06)',
              border: '1px dashed rgba(0, 194, 255, 0.25)',
              borderRadius: '8px',
              fontSize: '12px',
              color: 'var(--text)',
              lineHeight: 1.5,
            }}
          >
            <strong style={{ color: '#00C2FF' }}>
              {t('hedera.surveillance.nextExpected', 'Prochain release attendu')}
            </strong>{' '}
            — {NEXT_RELEASE.scheduleLabel} · ~{fmt(NEXT_RELEASE.estimatedHbar)} HBAR
            <span style={{ color: 'var(--muted)', marginLeft: '8px' }}>
              ({formatCountdown(countdown, locale, t)})
            </span>
          </div>
        )}
      </div>

      <BannerActions t={t} variant="surveillance" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Shared bits
// ─────────────────────────────────────────────────────────────────────

function BannerIcon({ variant }: { variant: 'release' | 'surveillance' }) {
  return (
    <div
      style={{
        width: '44px',
        height: '44px',
        borderRadius: '10px',
        background: 'linear-gradient(135deg, #000000, #00C2FF)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '22px',
        fontWeight: 700,
        color: 'white',
        flexShrink: 0,
        opacity: variant === 'surveillance' ? 0.85 : 1,
      }}
      aria-hidden
    >
      ℏ
    </div>
  );
}

function SupplyCell({ label, value }: { label: string; value: string }) {
  return (
    <span>
      {label} :{' '}
      <strong style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </strong>{' '}
      HBAR
    </span>
  );
}

function BannerActions({
  t,
  variant,
}: {
  t: (key: string, fallback: string) => string;
  variant?: 'surveillance';
}) {
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      <a
        href="https://hederacouncil.org/treasury"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 12px',
          background:
            variant === 'surveillance' ? 'transparent' : 'rgba(0, 194, 255, 0.12)',
          color: '#00C2FF',
          border: '1px solid rgba(0, 194, 255, 0.3)',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: 600,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        <Icons.ExternalLink size={12} />
        {t('hedera.release.officialReport', 'Rapport officiel')}
      </a>
      <a
        href="https://hashscan.io/mainnet/dashboard"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 12px',
          background: 'transparent',
          color: 'var(--muted)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: 600,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        <Icons.ExternalLink size={12} />
        HashScan
      </a>
    </div>
  );
}

function PulseDot() {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: '#00C2FF',
        boxShadow: '0 0 0 0 rgba(0, 194, 255, 0.6)',
        animation: 'hbarSurveillancePulse 2s ease-in-out infinite',
      }}
    />
  );
}

// Inject the pulse keyframes once.
if (typeof document !== 'undefined' && !document.getElementById('hbar-surveillance-pulse')) {
  const style = document.createElement('style');
  style.id = 'hbar-surveillance-pulse';
  style.textContent = `
    @keyframes hbarSurveillancePulse {
      0%   { box-shadow: 0 0 0 0 rgba(0, 194, 255, 0.5); }
      70%  { box-shadow: 0 0 0 6px rgba(0, 194, 255, 0); }
      100% { box-shadow: 0 0 0 0 rgba(0, 194, 255, 0); }
    }
  `;
  document.head.appendChild(style);
}

// ─────────────────────────────────────────────────────────────────────
// Time helpers
// ─────────────────────────────────────────────────────────────────────

interface Countdown {
  days: number;
  hours: number;
  minutes: number;
  isPast: boolean;
}

/**
 * Live countdown to `targetIso`. Ticks every minute (1 minute is the
 * smallest unit the banner displays, so re-rendering more often would
 * be wasteful).
 */
function useCountdown(targetIso: string): Countdown | null {
  const target = useMemo(() => new Date(targetIso).getTime(), [targetIso]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!Number.isFinite(target)) return;
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, [target]);
  if (!Number.isFinite(target)) return null;
  const diff = target - now;
  const absDiff = Math.abs(diff);
  return {
    days: Math.floor(absDiff / 86_400_000),
    hours: Math.floor((absDiff % 86_400_000) / 3_600_000),
    minutes: Math.floor((absDiff % 3_600_000) / 60_000),
    isPast: diff < 0,
  };
}

function formatCountdown(
  c: Countdown,
  locale: string,
  t: (key: string, fallback: string) => string,
): string {
  if (c.isPast) {
    return t('hedera.surveillance.overdue', 'attendu — en cours de détection');
  }
  // Most useful unit: days when far, hours when close, minutes when very close.
  if (c.days >= 2) {
    try {
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
      return rtf.format(c.days, 'day');
    } catch {
      return `dans ${c.days} j`;
    }
  }
  if (c.days >= 1) {
    return `${c.days} j ${c.hours} h`;
  }
  if (c.hours >= 1) {
    return `${c.hours} h ${c.minutes} min`;
  }
  return `${c.minutes} min`;
}

function relativeAgo(iso: string, locale: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diffMs)) return '';
  const minutes = Math.round(diffMs / 60_000);
  const hours = Math.round(diffMs / 3_600_000);
  const days = Math.round(diffMs / 86_400_000);

  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    if (minutes < 60) return rtf.format(-minutes, 'minute');
    if (hours < 48) return rtf.format(-hours, 'hour');
    return rtf.format(-days, 'day');
  } catch {
    if (minutes < 60) return `${minutes} min`;
    if (hours < 48) return `${hours} h`;
    return `${days} j`;
  }
}
